import { useEffect, useState } from "react";
import { type AiKeyState, ApiError, api } from "./api";
import { Alert, Box, Field, Working } from "./ui";

/**
 * The OpenAI key the reading of slabs and supplier invoices runs on, for a master to put
 * in, change or take out without anyone on the server (Daren, 6 October 2026). Only the
 * last four characters are ever shown back.
 */
export function AiKey(): React.ReactElement {
  const [state, setState] = useState<AiKeyState | undefined>();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [done, setDone] = useState<string | undefined>();

  useEffect(() => {
    void api
      .aiKey()
      .then((result) => setState(result.key))
      .catch(() => setError("Could not load the AI reading key"));
  }, []);

  async function save(value: string | null): Promise<void> {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      const result = await api.setAiKey(value);
      setState(result.key);
      setKey("");
      setDone(value === null ? "Removed. AI reading is off." : "Saved. AI reading uses the new key from now on.");
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The key could not be saved");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box title="AI reading key">
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {done ? <Alert tone="success">{done}</Alert> : null}
      <p className="text-[0.813rem] mb-3">
        {!state
          ? "Loading."
          : state.source === "off"
            ? "AI reading is off. Paste an OpenAI key to turn it on."
            : `AI reading is on, with the key ending ${state.lastFour}${
                state.source === "desk" && state.changedBy ? `, put in by ${state.changedBy}` : ", set on the server"
              }.`}
      </p>
      <Field id="ai-key" label="New OpenAI key" hint="Starts with sk-. It replaces the key in use and is never shown again.">
        <input
          id="ai-key"
          type="password"
          autoComplete="off"
          className="form-control"
          value={key}
          placeholder="sk-..."
          onChange={(event) => setKey(event.target.value)}
        />
      </Field>
      <div className="flex flex-wrap gap-2 mt-3">
        <button type="button" className="ti-btn ti-btn-primary-full !mb-0" disabled={busy || !key.trim()} onClick={() => void save(key)}>
          {busy ? <Working label="Saving" /> : "Save key"}
        </button>
        {state && state.source !== "off" ? (
          <button
            type="button"
            className="ti-btn ti-btn-light !mb-0"
            disabled={busy}
            onClick={() => {
              if (window.confirm("Remove the key? AI reading turns off until a new one is saved.")) void save(null);
            }}
          >
            Remove key
          </button>
        ) : null}
      </div>
    </Box>
  );
}
